<template>
  <div
    v-if="isLoading"
    class="pt-8"
    style="text-align: center;"
  >
    <ngc-loading-spinner
      size="medium"
      aria-label="Please wait"
    />
    <div class="gator-body-1 mt-4">
      {{ loaderMessage[0] }}
    </div>
  </div>
  <!-- Custom handling for the closing portal (uses custom text from the new auth flow, and adds a back action to one state)-->
  <div v-else-if="(isEvault) && ['auth_sign_in', 'auth_input_email', 'auth_send_verification', 'auth_send_verification_email_only', 'auth_waiting_for_verification', 'auth_complete'].includes(state)">
    <div v-if="state.includes('auth_sign_in')">
      <ngc-input-label
        class="mb-2 gator-body-1"
        data-cy="email-label"
      >
        Email
      </ngc-input-label>
      <ngc-input-text
        id="email"
        type="email"
        data-cy="email-field"
        :autofocus="true"
        :value="current_state_definition.fields[0].response || ''"
        :error-text="current_state_definition.fields[0].error || ''"
        @input="current_state_definition.fields[0].response = $event.detail.value"
        @focus="clearErrors(0)"
        @blur="checkErrors(0)"
        @keydown.enter="keydownEnter()"
      />
      <ngc-input-label
        class="mb-2 mt-3 gator-body-1"
        data-cy="password-label"
      >
        Password
      </ngc-input-label>
      <ngc-input-text
        id="password"
        type="password"
        class="mb-3"
        data-cy="password-field"
        :name="current_state_definition.fields[1].hint"
        :disabled="current_state_definition.fields[1].disabled"
        :help-text="current_state_definition.fields[1].requirements"
        :value="current_state_definition.fields[1].response || ''"
        :error-text="current_state_definition.fields[1].error || ''"
        @input="current_state_definition.fields[1].response = $event.detail.value"
        @focus="clearErrors(1)"
        @blur="checkErrors(1)"
        @keydown.enter="keydownEnter()"
      />
      <ngc-alert
        v-if="error"
        variant="error"
        data-cy="login-error-message"
      >
        {{ errorMessages ? errorMessages[0] : '' }}
      </ngc-alert>
      <div class="forgot-password-row">
        <ngc-button
          variant="base"
          data-cy="forgot-password-link"
          size="medium"
          @click="nextState(current_state_definition.secondary_action);"
        >
          Forgot Password?
        </ngc-button>
      </div>
      <div :class="authCardFooterClasses">
        <ngc-button
          variant="primary"
          aria-label="sign in"
          data-cy="sign-in-btn"
          size="large"
          :loading="waitingRequests['sign_in']"
          :disabled="waitingRequest && !waitingRequests['sign_in']"
          @click="nextState(current_state_definition.primary_action);"
        >
          Sign In
        </ngc-button>
      </div>
    </div>
    <div v-else-if="state.includes('auth_input_email')">
      <div
        class="gator-heading-h4 mb-3"
        data-cy="recover-password-title"
      >
        Recover your password.
      </div>
      <ngc-input-label
        class="mb-2 gator-body-1"
        data-cy="recover-email-label"
      >
        Email
      </ngc-input-label>
      <ngc-input-text
        id="email"
        type="email"
        data-cy="recover-email-field"
        :autofocus="true"
        :value="current_state_definition.fields[0].response || ''"
        :error-text="current_state_definition.fields[0].error || ''"
        @input="current_state_definition.fields[0].response = $event.detail.value"
        @focus="clearErrors(0)"
        @blur="checkErrors(0)"
        @keydown.enter="keydownEnter()"
      />
      <ngc-alert
        v-if="error"
        variant="error"
        data-cy="login-error-message"
      >
        {{ errorMessages ? errorMessages[0] : '' }}
      </ngc-alert>
      <div :class="authCardFooterClasses">
        <ngc-button
          data-cy="back"
          size="large"
          :variant="isMobile ? 'secondary' : 'base'"
          :class="{ 'full-width-btn': isMobile }"
          :start-icon="isMobile ? '' : 'arrow-left'"
          @click="nextState(current_state_definition.secondary_action);"
        >
          Back
        </ngc-button>
        <ngc-button
          variant="primary"
          aria-label="continue"
          data-cy="continue"
          size="large"
          :class="{ 'full-width-btn': isMobile }"
          :loading="waitingRequests['connect']"
          :disabled="waitingRequest && !waitingRequests['connect']"
          @click="nextState(current_state_definition.primary_action)"
        >
          Continue
        </ngc-button>
      </div>
    </div>
    <div v-else-if="state.includes('auth_send_verification') || state.includes('auth_send_verification_email_only')">
      <div
        class="gator-heading-h4 mb-3"
        data-cy="send-code-message"
      >
        Where would you like us to send your verification code?
      </div>
      <ngc-radio-group
        v-if="current_state_definition.fields[0].options"
        required
        data-cy="radio-option"
        class="auth-selection-boxes"
        :value.prop="current_state_definition.fields[0].response"
        @change="current_state_definition.fields[0].response = $event.detail"
      >
        <ngc-radio
          v-for="(option, index) in filteredOptions(current_state_definition.fields[0].options)"
          :key="`option-${index}`"
          class="auth-selection-box"
          :class="{ 'auth-selection-box--selected': current_state_definition.fields[0].response === option.value }"
          :value="option.value"
        >
          <span class="auth-selection-box-label">{{ option.label }}</span>
        </ngc-radio>
      </ngc-radio-group>
      <ngc-alert
        v-if="error"
        variant="error"
        data-cy="login-error-message"
      >
        {{ errorMessages ? errorMessages[0] : '' }}
      </ngc-alert>
      <div :class="authCardFooterClasses">
        <ngc-button
          data-cy="back"
          size="large"
          :variant="isMobile ? 'secondary' : 'base'"
          :class="{ 'full-width-btn': isMobile }"
          :start-icon="isMobile ? '' : 'arrow-left'"
          @click="(() => this.$router.go())"
        >
          Back
        </ngc-button>
        <ngc-button
          variant="primary"
          aria-label="continue"
          data-cy="continue"
          size="large"
          :class="{ 'full-width-btn': isMobile }"
          :loading="waitingRequests['send_code']"
          :disabled="waitingRequest && !waitingRequests['send_code']"
          @click="nextState(current_state_definition.primary_action)"
        >
          Continue
        </ngc-button>
      </div>
    </div>
    <div v-else-if="state.includes('auth_waiting_for_verification')">
      <div
        class="gator-heading-h4 mb-3"
        data-cy="code-sent-message"
      >
        Your code was sent. Please enter it below:
      </div>
      <ngc-input-label
        class="mb-2 gator-body-1"
        data-cy="code-label"
      >
        6-digit Verification Code
      </ngc-input-label>
      <ngc-input-text
        id="code"
        data-cy="code-field"
        :autofocus="true"
        :value="current_state_definition.fields[0].response || ''"
        :error-text="current_state_definition.fields[0].error || ''"
        @input="current_state_definition.fields[0].response = $event.detail.value"
        @focus="clearErrors(0)"
        @blur="checkErrors(0)"
        @keydown.enter="keydownEnter()"
      />
      <ngc-checkbox
        class="auth-remember-checkbox"
        :selected.prop="current_state_definition.fields[1].response"
        @change="current_state_definition.fields[1].response = $event.detail.selected"
      >
        <span class="auth-remember-label">{{ current_state_definition.fields[1].label }}</span>
      </ngc-checkbox>

      <ngc-alert
        v-if="error"
        variant="error"
        data-cy="code-error-message"
      >
        {{ errorMessages ? errorMessages[0] : '' }}
      </ngc-alert>
      <div :class="authCardFooterClasses">
        <ngc-button
          data-cy="back"
          size="large"
          :variant="isMobile ? 'secondary' : 'base'"
          :class="{ 'full-width-btn': isMobile }"
          :start-icon="isMobile ? '' : 'arrow-left'"
          @click="nextState(current_state_definition.secondary_action);"
        >
          Back
        </ngc-button>
        <ngc-button
          variant="primary"
          aria-label="verify"
          data-cy="verify-code-btn"
          size="large"
          :class="{ 'full-width-btn': isMobile }"
          :loading="waitingRequests['verify']"
          :disabled="waitingRequest && !waitingRequests['verify']"
          @click="nextState(current_state_definition.primary_action);"
        >
          Continue
        </ngc-button>
      </div>
    </div>
    <div v-else-if="state.includes('auth_complete')">
      <div
        class="gator-heading-h4 mb-3 auth-message-centered"
        data-cy="all-set-message"
      >
        You're all set!
      </div>
      <ngc-alert
        v-if="error"
        variant="error"
        data-cy="login-error-message"
      >
        {{ errorMessages ? errorMessages[0] : '' }}
      </ngc-alert>

      <div :class="authCardFooterClasses">
        <ngc-button
          v-if="pendingPasswordResetAction"
          data-cy="password-reset"
          size="large"
          :variant="isMobile ? 'secondary' : 'base'"
          :class="{ 'full-width-btn': isMobile }"
          @click="nextState(pendingPasswordResetAction);"
        >
          Reset password
        </ngc-button>
        <ngc-button
          variant="primary"
          aria-label="complete"
          data-cy="complete"
          size="large"
          :class="{ 'full-width-btn': isMobile }"
          :loading="waitingRequests['complete']"
          :disabled="waitingRequest && !waitingRequests['complete']"
          @click="nextState(current_state_definition.primary_action);"
        >
          Continue
        </ngc-button>
      </div>
    </div>
  </div>
  <!-- This block handles all other state combinations -->
  <div v-else>
    <div
      v-if="current_state_definition.title"
      class="gator-heading-h4 mb-3"
      :class="{ 'auth-message-centered': !hasFields }"
      data-cy="generic-title"
    >
      {{ current_state_definition.title }}
    </div>
    <div
      v-if="current_state_definition.subtitle"
      class="gator-body-1 mb-3"
    >
      {{ current_state_definition.subtitle }}
    </div>
    <div
      v-for="(field,index) in current_state_definition.fields"
      :key="'field-' + index"
    >
      <div
        v-if="field.text"
        id="content"
        v-html="field.text"
      />
      <ngc-radio-group
        v-if="field.options"
        :value.prop="field.response"
        required
        data-cy="lo-list"
        class="mt-2"
        @change="field.response = $event.detail"
      >
        <ngc-radio
          v-for="(option, index) in filteredOptions(field.options)"
          :key="`option-${index}`"
          :data-cy="option.label ? getCypressTag(option.label + '-input') : 'radio-input'"
          :value="option.value"
          :title-text="option.label"
        />
      </ngc-radio-group>
      <div v-if="!field.options && (field.type !== 'phone' && field.type !== 'password' && field.type !== 'checkbox')">
        <ngc-input-label
          class="mb-2 gator-body-1"
          :data-cy="field.type + '-label'"
        >
          {{ field.hint }}
        </ngc-input-label>
        <ngc-input-text
          type="text"
          :autofocus="index === 0"
          :class="'sel_input_' + index"
          :disabled="field.disabled"
          :error-text="field.error ? field.error : ''"
          :data-cy="field.type + '-field'"
          :name="field.hint"
          :value="field.response || ''"
          @input="field.response = $event.detail.value"
          @focus="clearErrors(index)"
          @blur="checkErrors(index)"
          @keydown.enter="keydownEnter()"
        />
      </div>
      <div v-if="!field.options && field.type === 'phone'">
        <ngc-input-label
          class="mb-2 gator-body-1"
        >
          {{ field.hint }}
        </ngc-input-label>
        <ngc-input-text
          placeholder="(___) ___-____"
          :error-text="field.error ? field.error : ''"
          :disabled="field.disabled"
          :value="field.response || ''"
          @input="field.response = $event.detail.value"
          @focus="clearErrors(index)"
          @blur="checkErrors(index)"
          @keydown.enter="keydownEnter()"
        />
      </div>
      <div v-if="field.type === 'password'">
        <ngc-input-label
          class="mb-2 mt-3 gator-body-1"
          :data-cy="field.type + '-label'"
        >
          {{ field.hint }}
        </ngc-input-label>
        <ngc-input-text
          :id="field.hint"
          type="password"
          class="mb-3"
          placeholder=""
          :name="field.hint"
          :disabled="field.disabled"
          :help-text="field.requirements || ''"
          :data-cy="field.type + '-field'"
          :value="field.response || ''"
          :error-text="field.error ? field.error : ''"
          @input="field.response = $event.detail.value"
          @focus="clearErrors(index)"
          @blur="checkErrors(index)"
          @keydown.enter="keydownEnter()"
        />
      </div>
      <ngc-checkbox
        v-if="field.type === 'checkbox'"
        class="mt-2"
        :selected.prop="field.response"
        :title-text="field.label"
        @change="field.response = $event.detail.selected"
      />
    </div>
    <ngc-alert
      v-if="error"
      variant="error"
      data-cy="login-error-message"
    >
      {{ errorMessages ? errorMessages[0] : '' }}
    </ngc-alert>
    <div
      v-if="orderedActions.length"
      :class="authCardFooterClasses"
    >
      <ngc-button
        v-for="(action, index) in orderedActions"
        :key="'action-' + index"
        :variant="action === current_state_definition.primary_action ? 'primary' : isMobile ? 'secondary' : 'base'"
        :class="{ 'full-width-btn': isMobile }"
        :aria-label="formatActionLabel(action.label)"
        :data-cy="getCypressTag(action.value)"
        size="large"
        :loading="waitingRequests[action.value]"
        :disabled="waitingRequest && !waitingRequests[action.value]"
        @click="nextState(action);"
      >
        {{ formatActionLabel(action.label) }}
      </ngc-button>
    </div>
  </div>
</template>

<script>
import snValidationsService from '~/services/snValidationsService.js'
import cookie from 'js-cookie'

export default {

  name: 'StatefulAuth',

  props: {
    activationCode: {
      type: String,
      default: '',
      required: false
    },
    autoLogin: {
      type: Boolean,
      default: false,
      required: false
    },
    userEmail: {
      type: String,
      default: '',
      required: false
    },
    init: {
      type: String,
      default: '',
      required: false
    },
    supressRedirects: {
      type: Boolean,
      default: false,
      required: false
    },
    authContext: {
      type: String,
      default: '',
      required: false
    },
    inviteGuid: {
      type: String,
      default: '',
      required: false
    },
    currentState: {
      type: Object,
      default: () => { return { fields: [] } },
      required: false
    },
    initState: {
      type: String,
      default: 'init',
      required: false
    },
    ssoToken: {
      type: String,
      default: '',
      required: false
    },
    enoteGuid: {
      type: String,
      default: '',
      required: false
    },
    consentClientId: {
      type: String,
      default: '',
      required: false
    },
    consentRedirect: {
      type: String,
      default: '',
      required: false
    },
    consentState: {
      type: String,
      default: '',
      required: false
    }
  },

  data () {
    return {
      email: null,
      token: null,
      current_state_definition: this.currentState,
      actions: {},
      state: this.initState,
      titleText: '',
      subtitleText: '',
      values: [],
      error: false,
      errorMessages: [],
      waitingRequest: false,
      waitingRequests: {},
      autoAdvancing: false,
      hasSkippedCompleteScreen: false,
      pingEpoch: 0,
      rules: {
        email: (value) => {
          if (!value) return 'Enter your email address'
          return snValidationsService.email(value) || 'Enter a valid email address'
        },
        phone: (value) => {
          return snValidationsService.phone(value) || 'Enter a valid phone number'
        }
      }
    }
  },

  computed: {
    authParams () {
      return {
        from_web: true,
        auth_context: this.authContext,
        invite_guid: this.inviteGuid,
        sso_token: this.ssoToken
      }
    },
    isMobile () {
      return this.$vuetify.breakpoint.mdAndDown
    },
    isEvault () {
      return this.authContext === 'evault'
    },
    isSkippableAuthContext () {
      return ['evault'].includes(this.authContext)
    },
    pendingPasswordResetAction () {
      const extras = this.current_state_definition.additional_actions
      if (!Array.isArray(extras)) return null
      return extras.find(action => action.value === 'request_new_password') || null
    },

    hasFields () {
      return !!this.current_state_definition.fields &&
        this.current_state_definition.fields.length > 0
    },

    orderedActions () {
      if (!Array.isArray(this.actions)) return []
      const primary = this.current_state_definition.primary_action
      if (!primary) return this.actions.slice()
      return [...this.actions.filter(action => action !== primary), primary]
    },
    authCardFooterClasses () {
      return ['auth-card-footer', { 'auth-card-footer--stacked': this.isMobile }]
    },
    loaderMessage () {
      return ['Please wait...']
    },
    isLoading () {
      return this.autoAdvancing || this.state === 'init' || this.state === 'requestSsoSignIn' || this.state === 'auth_signed_in' || this.state.includes('auth_sso_sign_in') || this.state.includes('auth_sso_signed_in')
    }
  },

  // Usually the titles for each state are displayed in a parent component so this controls that display
  watch: {
    titleText () {
      this.$emit('title', this.titleText)
    },
    subtitleText () {
      this.$emit('subtitle', this.subtitleText)
    },
    state (newVal) {
      this.$emit('change-state', this.state)
      if (this.state === 'auth_sign_in' && this.$route && this.$route.query.email) {
        this.current_state_definition.fields[0].response = this.$route.query.email
      }
    },
    init (newVal) {
      if (newVal === 'start') {
        this.getStarted(true)
      }
    }
  },

  created () {
    this.getStarted(this.init === 'start', !!this.ssoToken)
    if (this.autoLogin) {
      if (window.PasswordCredential) {
        navigator.credentials.get({
          password: true,
          mediation: 'required'
        }).then(c => {
          if (c) {
            this.current_state_definition.fields[0].response = c.id
            this.current_state_definition.fields[1].response = c.password
            this.nextState()
          }
        }).catch(error => {
          // various browsers throw a NotSupportedError if they don't support credentials
          console.log('credential management is not supported')
          return error
        })
      }
    }
  },
  beforeDestroy () {
    window.removeEventListener('beforeunload', this.confirmLeavePage)
  },

  methods: {
    formatActionLabel (label) {
      if (!label || label !== label.toUpperCase()) return label
      return label.toLowerCase().replace(/(^|\s)\S/g, c => c.toUpperCase())
    },

    keydownEnter () {
      if (!this.waitingRequest) {
        this.nextState()
      }
    },

    handleServicerChoice (actionValue, selectedSac) {
      const action = {
        key: 'event',
        value: actionValue
      }
      if (actionValue === 'save_servicer') {
        this.current_state_definition.fields[0].response = selectedSac
        this.activationCode = selectedSac
        this.$emit('change-activation-code', selectedSac)
        this.nextState(action)
      }
      if (actionValue === 'skip_selection') {
        this.current_state_definition.fields[0].response = this.activationCode
        this.nextState(action)
      }
    },

    createRequest (action) {
      // This function goes through the current state definition and creates a request
      // based on keys and values and the event based on the action passed to this function
      const request = this.authParams
      request.auth_guid = cookie.get('evault_auth_guid')
      if (this.activationCode) { // pass this through as soon as possible otherwise redirect to borrower/signup
        request.activation_code = this.activationCode
      }
      if (this.state.includes('choose_expert')) {
        request.event = action.value
        request.activation_code = this.activationCode
        return request
      }
      if (action) {
        request.event = action.value
        if (this.current_state_definition.fields) {
          for (let x = 0; x < this.current_state_definition.fields.length; x++) {
            request[this.current_state_definition.fields[x].key] = this.current_state_definition.fields[x].response
          }
        }
      }
      return request
    },

    checkErrors (index, checkUndefined) {
      checkUndefined = false
      // Check the rules for different types of fields for validation of input

      // Check to see if we are looking at valid field
      if (this.current_state_definition.fields && this.current_state_definition.fields[index] === undefined) {
        return false
      }

      // Trim imput
      if (this.current_state_definition.fields[index].response !== '' && this.current_state_definition.fields[index].response !== undefined) this.current_state_definition.fields[index].response = this.current_state_definition.fields[index].response.trim()

      // Run custom validation
      if (this.rules[this.current_state_definition.fields[index].type]) {
        if (this.rules[this.current_state_definition.fields[index].type](this.current_state_definition.fields[index].response) !== true) {
          this.$set(this.current_state_definition.fields[index], 'ruleError', true)
          this.current_state_definition.fields[index].error = this.rules[this.current_state_definition.fields[index].type](this.current_state_definition.fields[index].response)
          return true
        }
      }
      return false
    },

    clearErrors (index) {
      this.$set(this.current_state_definition.fields[index], 'ruleError', false)
      if (this.current_state_definition.fields[index].response === undefined) {
        this.current_state_definition.fields[index].response = ''
      }

      let removeErrorMessage = true
      for (let x = 0; x < this.current_state_definition.fields.length; x++) {
        if (this.current_state_definition.fields[x].ruleError === true) {
          removeErrorMessage = false
        } else {
          this.$set(this.current_state_definition.fields[x], 'error', '')
        }
      }
      if (removeErrorMessage === true) {
        this.errorMessages = []
        this.error = false
      }
    },

    // Redirect to sessions controller with token
    finishedAuthentication () {
      this.$axios.post('/sessions', {
        token: this.token,
        email: this.email,
        authenticity_token: document.getElementsByName('csrf-token')[0].content,
        fromLogin: true,
        fromMobileFlow: true,
        fromMobileShare: (this.$route?.query?.from_mobile_share === 'true'),
        sac_id: this.sacId,
        user_id: this.userId,
        enote_guid: this.enoteGuid,
        consent_client_id: this.consentClientId,
        consent_redirect: this.consentRedirect,
        consent_state: this.consentState
      })
        .then(
          (response) => {
            if (!this.supressRedirects) {
              window.location = response.data
            }
            this.$emit('authenticated')
          }
        )
        .catch(
          (error) => {
            this.$sentry.captureException(error)
            console.log(error)
            this.clearLoaders()
            this.error = true
            this.errorMessages = ['Something went wrong. Please try again.']
          }
        )
    },

    clearLoaders () {
      this.waitingRequest = false
      Object.keys(this.waitingRequests).forEach((key) => {
        this.$set(this.waitingRequests, key, false)
      })
    },

    ping () {
      const epoch = this.pingEpoch
      const url = '/auth/v1/ping/' + cookie.get('evault_auth_guid')
      this.$axios.get(url)
        .then(
          (response) => {
            if (epoch !== this.pingEpoch) return
            if (response.data.current_state !== 'auth_waiting_for_verification') {
              clearInterval(this.pingFunction) // Once we have moved forward in the flow this stops the ping requests from continuing
              window.removeEventListener('beforeunload', this.confirmLeavePage) // remove event listener warning user to not leave page
              if (this.state === 'auth_waiting_for_verification') {
                this.$axios.post('/auth/v1/sync', this.createRequest(null))
                  .then((response) => {
                    if (epoch !== this.pingEpoch) return
                    this.processAuthResponse(response)
                  })
              }
            }
          }
        )
    },

    nextState (action) {
      // ensure the ping and beforeunload listener are immediately cleared on state transition.
      clearInterval(this.pingFunction)
      this.pingEpoch += 1
      window.removeEventListener('beforeunload', this.confirmLeavePage)

      if (action === undefined || action === null || action === '') {
        action = this.current_state_definition.primary_action
      }

      if (action?.value === 'start_over') {
        this.setLoader(action, true)
        window.location = '/login'
        return
      }

      if (this.current_state_definition.fields && this.state !== 'auth_waiting_for_verification' && this.state.includes('choose_expert') && action === this.current_state_definition.primary_action) {
        let errorsExist = false
        for (let x = 0; x < this.current_state_definition.fields.length; x++) {
          // Make sure all the fields have input
          if (!this.current_state_definition.fields[x].response) {
            this.$set(this.current_state_definition.fields[x], 'ruleError', true)
            errorsExist = true
            this.error = true
            this.errorMessages = ['All fields are required']
          } else { // If any fields still have errors don't move to the next state
            errorsExist = this.checkErrors(x, true)
          }
        }
        if (errorsExist) {
          return
        }
      }
      this.setLoader(action, true)
      this.$axios.post('/auth/v1/sync', this.createRequest(action))
        .then(
          (response) => {
            if (!this.processAuthResponse(response)) {
              this.setLoader(action, false)
            }
          }
        )
        .catch((error) => {
          this.$sentry.captureException(error)
          this.setLoader(action, false)
          this.autoAdvancing = false
          this.error = true
          this.errorMessages = ['Something went wrong. Please try again.']
        })
    },

    setLoader (action, value) {
      this.waitingRequest = value
      if (action?.value) {
        this.$set(this.waitingRequests, action.value, value)
      }
    },

    // Currently we are skipping the init state used on mobile
    getStarted (init = false, sso = false) {
      // return
      cookie.set('evault_new_user', true)
      this.$axios.post('/auth/v1/init', this.authParams)
        .then(
          (response) => { // this could be changed if we switch to the full state machine
            // we are using set instead of setting equal to in order to allow vue to responsively react to the changes of the values within the keys.
            // for more info: https://vuejs.org/v2/guide/reactivity.html#Change-Detection-Caveats
            const keys = Object.keys(response.data.global_action_definitions)
            keys.forEach(key => {
              this.$set(this.current_state_definition, key, response.data.global_action_definitions[key])
            })
            cookie.set('evault_auth_guid', response.data.values.auth_guid)
            if (init) {
              this.nextState(response.data.global_action_definitions.primary_action)
            } else if (sso) {
              this.nextState(response.data.global_action_definitions.sso_action)
            } else {
              this.nextState(response.data.global_action_definitions.secondary_action)
            }
          }
        )
    },

    setActions () {
      this.actions = []
      if (this.current_state_definition.primary_action) {
        this.actions = [this.current_state_definition.primary_action] // These are the buttons that are displayed
      }
      if (this.current_state_definition.secondary_action) {
        this.actions = this.actions.concat(this.current_state_definition.secondary_action)
      }
      if (this.current_state_definition.additional_actions) { // If there are additional actions besides main action
        this.actions = this.actions.concat(this.current_state_definition.additional_actions)
      }
    },

    setFields () {
      for (let x = 0; x < this.current_state_definition.fields.length; x++) {
        const field = this.current_state_definition.fields[x]
        this.$set(field, 'response', this.values[field.key])
        // ngc-radio-group has no equivalent of sn-radio-group's `mandatory`, so
        // default radio fields to the first option to preserve that behaviour.
        if (field.options && !field.response) {
          const [firstOption] = this.filteredOptions(field.options)
          if (firstOption) this.$set(field, 'response', firstOption.value)
        }
      }
    },

    setErrors () {
      this.errorMessages = [this.current_state_definition.error]
      if (this.errorMessages && this.errorMessages[0] !== '') this.error = true
      else {
        this.error = false
        this.errorMessages = []
      }
    },

    processAuthResponse (response) {
      const nextState = response.data.current_state
      // the state will be expired if the auth has completed or a time out, so the check for authenticated_data is necessary
      if ((nextState === 'auth_signed_in' || nextState === 'auth_sso_signed_in') && response.data.authenticated_data) {
        this.email = response.data.authenticated_data.user.email
        this.token = response.data.authenticated_data.token
        this.sacId = response.data.authenticated_data.sac_id
        this.userId = response.data.authenticated_data.user.id
        this.finishedAuthentication()
        return true
      }
      // Set current state
      this.state = nextState
      if (this.state === 'init') {
        this.$emit('title', 'With a little information, we\'ll save your work for you.')
        this.getStarted()
        return false
      }

      this.values = response.data.values // gets current preset values such as email, phone name ect. These are displayed as helps to user if fields match
      this.current_state_definition = response.data.current_state_definition || { fields: [] }
      this.setActions()
      this.setFields()
      this.setErrors()

      if (this.state === 'auth_sign_in') {
        this.hasSkippedCompleteScreen = false
      }

      if (this.state === 'auth_complete' && this.pendingPasswordResetAction && !this.hasSkippedCompleteScreen) {
        this.hasSkippedCompleteScreen = true
        this.autoAdvancing = true
        this.nextState(this.pendingPasswordResetAction)
        return true
      }
      this.autoAdvancing = false

      this.titleText = this.current_state_definition.title
      this.subtitleText = this.current_state_definition.subtitle
      this.fieldHeader = this.current_state_definition.field_header
      // If we are waiting for verification we starting pinging so if they click on the email link we can advance
      if (this.state === 'auth_waiting_for_verification') {
        window.addEventListener('beforeunload', this.confirmLeavePage)
        clearInterval(this.pingFunction)
        this.pingFunction = setInterval(function () {
          this.ping()
        }.bind(this), response.data.current_state_definition.refresh_rate)
      }
      // If we are passed the email from another component already (such as borrowerInitial) this will move us forward in the state machine.
      if (this.userEmail && this.state === 'new_input_email' && response.data.success === true) {
        this.current_state_definition.fields[0].response = this.userEmail
        this.state = 'init'
        this.nextState()
      }

      // SSO from monolith to microservice
      if (this.state === 'auth_sso_sign_in' && response.data.success === true) {
        this.nextState(this.current_state_definition.primary_action)
      }
    },

    filteredOptions (options) {
      return options.filter((option) => option.label !== '')
    },

    confirmLeavePage (event) {
      // The following set up should work with the following browsers: IE, Edge, Chrome, Safari, Firefox.
      // **NOTE: Only IE allows you to customize the message of the alert. The other
      //         browsers use a standard message notifying the user of unsaved changes.***
      const message = 'Leaving this page now may disrupt the process. Do you still want to leave?'
      event.returnValue = message
      return message
    },

    getCypressTag (type) {
      switch (type) {
        case 'sign_in':
          return 'sign-in-btn'
        case 'save_servicer':
          return 'NEXT-btn'
        case 'I agree-input':
          return 'agree-input'
        case 'complete':
          return 'continue'
        default:
          return type
      }
    }
  }

}
</script>

<style scoped>
  .forgot-password-row {
    width: 100%;
    display: flex;
    justify-content: flex-start;
  }
  .auth-card-footer {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 16px;
    margin: 32px -32px -32px;
    padding: 16px;
    background-color: var(--color-surface-secondary, #F7F7F7);
  }

  .auth-card-footer--stacked {
    flex-direction: column;
    gap: 8px;
  }

  .auth-message-centered {
    text-align: center;
  }

  .auth-remember-checkbox {
    --ngc-checkbox-margin-y: 0;
    display: block;
    margin-top: calc(1rem - 0.2rem);
  }

  .auth-remember-label {
    position: relative;
    top: 1px;
  }

  .auth-selection-boxes {
    display: block;
    margin-bottom: -8px;
  }

  .auth-selection-box {
    box-sizing: border-box;
    display: flex;
    align-items: center;
    width: 100%;
    min-height: 72px;
    padding: 16px 16px 16px 8px;
    border: 1px solid var(--color-border-primary, #6D6D6D);
    border-radius: var(--border-radius-x-small, 2px);
    background-color: var(--color-surface-primary, #FFFFFF);
    cursor: pointer;
  }

  .auth-selection-box--selected {
    border-color: var(--color-border-brand, #103656);
    background-color: var(--color-surface-brand-secondary, #DAE7F1);
  }

  .auth-selection-box-label {
    margin-left: 8px;
  }
  .full-width-btn {
    display: block;
    width: 100%;
    --ngc-button-width: 100%;
  }
  .full-width-btn::part(button) {
    width: 100%;
  }
</style>
